/**
 * MSAL Configuration
 * Microsoft Authentication Library configuration for Azure AD and Azure Entra External ID authentication
 * Supports both regular Azure AD and Azure Entra External ID (CIAM/B2C)
 */

import { Configuration, PopupRequest, RedirectRequest, SilentRequest, AccountInfo } from '@azure/msal-browser';
import { shouldEnableMsalPopupFallback } from '../utils/featureFlags.utils';

/**
 * SECURE URL VALIDATION HELPERS
 * These functions securely validate URL domains to prevent subdomain attacks.
 * Instead of using .includes() which can match substrings anywhere in the URL,
 * these functions validate that the hostname ends with the expected domain.
 */

/**
 * Securely check if a URL is a CIAM login domain (.ciam.login.microsoftonline.com)
 * Validates that hostname ends with .ciam.login.microsoftonline.com
 */
export function isCiamLoginDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return hostname.endsWith('.ciam.login.microsoftonline.com') || hostname === 'ciam.login.microsoftonline.com';
  } catch {
    // Invalid URL - use regex fallback
    return /^https:\/\/[^/]+\.ciam\.login\.microsoftonline\.com(\/|$)/i.test(url);
  }
}

/**
 * Securely check if a URL is a CIAM custom domain (.ciamlogin.com)
 * Validates that hostname ends with .ciamlogin.com
 */
export function isCiamCustomDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return hostname.endsWith('.ciamlogin.com') || hostname === 'ciamlogin.com';
  } catch {
    // Invalid URL - use regex fallback
    return /^https:\/\/[^/]+\.ciamlogin\.com(\/|$)/i.test(url);
  }
}

/**
 * Securely check if a URL is a B2C login domain (.b2clogin.com)
 * Validates that hostname ends with .b2clogin.com
 */
export function isB2CLoginDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return hostname.endsWith('.b2clogin.com') || hostname === 'b2clogin.com';
  } catch {
    // Invalid URL - use regex fallback
    return /^https:\/\/[^/]+\.b2clogin\.com(\/|$)/i.test(url);
  }
}

/**
 * Azure Entra External ID Configuration
 * Determines if using Azure Entra External ID (CIAM or B2C) vs regular Azure AD
 */
export const isExternalId = (): boolean => {
  const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? '';
  return (
    isCiamLoginDomain(authority) ||
    isCiamCustomDomain(authority) ||
    isB2CLoginDomain(authority)
  );
};

/**
 * Get the base authority URL (without user flow/policy)
 * For External ID, extracts the base authority from the full authority URL
 */
export const getBaseAuthority = (): string => {
  const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? 'https://login.microsoftonline.com/common';
  
  // If using External ID with user flow, extract base authority
  if (isExternalId()) {
    // For B2C: https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/{policy}
    // For CIAM: https://{tenant}.ciam.login.microsoftonline.com/{tenant-id}
    // Extract base part before the policy/user flow
    const match = authority.match(/^https:\/\/[^/]+(?:\/[^/]+)?/);
    if (match) {
      return match[0];
    }
  }
  
  return authority;
};

/**
 * Get the domain authority for knownAuthorities (CIAM only)
 * For CIAM, knownAuthorities should be just the domain, not including tenant-id
 */
export const getKnownAuthorityDomain = (): string | undefined => {
  const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? '';
  
  if (isCiamLoginDomain(authority)) {
    // Extract just the domain: https://{tenant}.ciam.login.microsoftonline.com
    const match = authority.match(/^(https:\/\/[^/]+\.ciam\.login\.microsoftonline\.com)/);
    if (match) {
      return match[1];
    }
  }
  
  // For External ID CIAM with custom domain format (.ciamlogin.com)
  // knownAuthorities should be the full base authority INCLUDING tenant-id
  // This is required for MSAL to resolve endpoints correctly for custom CIAM domains
  if (isCiamCustomDomain(authority) && !isCiamLoginDomain(authority)) {
    // Extract base authority: https://{tenant}.ciamlogin.com/{tenant-id}
    // For .ciamlogin.com format, we need the full base authority as knownAuthorities
    const match = authority.match(/^(https:\/\/[^/]+\.[^/]+\/[^/]+)/);
    if (match) {
      const baseAuthority = match[1];
      if (import.meta.env.DEV) {
        console.log('[MSAL Config] Extracted knownAuthority (custom CIAM domain with tenant-id):', baseAuthority);
      }
      return baseAuthority;
    }
  }
  
  if (isB2CLoginDomain(authority)) {
    // For B2C, extract the domain: https://{tenant}.b2clogin.com
    const match = authority.match(/^(https:\/\/[^/]+\.b2clogin\.com)/);
    if (match) {
      return match[1];
    }
  }
  
  if (import.meta.env.DEV) {
    console.warn('[MSAL Config] Could not extract knownAuthority domain from:', authority);
  }
  return undefined;
};

/**
 * Get user flow/policy name from authority or environment variable
 * Used for Azure Entra External ID (B2C/CIAM)
 */
export const getUserFlow = (): string | undefined => {
  // Check environment variable first
  const envUserFlow = import.meta.env.VITE_AZURE_USER_FLOW;
  if (envUserFlow) {
    return envUserFlow;
  }
  
  // Extract from authority URL if using B2C format
  const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? '';
  if (isB2CLoginDomain(authority)) {
    const match = authority.match(/\/([^/]+)$/);
    if (match) {
      return match[1];
    }
  }
  
  return undefined;
};

/**
 * Build authority URL with user flow if needed
 * For External ID, appends user flow to base authority
 * 
 * IMPORTANT: For custom CIAM domains (.ciamlogin.com), the user flow should NOT be appended
 * to the authority used for MSAL initialization, as the metadata endpoint doesn't include it.
 * The user flow is only used in the login/token requests (handled in createLoginRequest).
 */
export const buildAuthority = (): string => {
  const baseAuthority = getBaseAuthority();
  const userFlow = getUserFlow();
  
  // If using External ID and user flow is specified, append it
  if (isExternalId() && userFlow) {
    // For B2C format: base authority already includes tenant, just append policy
    if (isB2CLoginDomain(baseAuthority)) {
      return `${baseAuthority}/${userFlow}`;
    }
    // For standard CIAM format: append user flow
    if (isCiamLoginDomain(baseAuthority)) {
      return `${baseAuthority}/${userFlow}`;
    }
    // For custom CIAM domains (.ciamlogin.com): DO NOT append user flow to authority
    // The metadata endpoint is at the base authority level without user flow
    // User flow is specified in login requests via extraQueryParameters instead
    if (isCiamCustomDomain(baseAuthority)) {
      if (import.meta.env.DEV) {
        console.log('[MSAL Config] Custom CIAM domain detected - user flow will be passed in login requests only');
      }
      return baseAuthority; // Return base authority WITHOUT user flow
    }
  }
  
  return baseAuthority;
};

/**
 * Azure AD / Azure Entra External ID Application Configuration
 * These values should be set in your environment variables
 * 
 * For Azure Entra External ID:
 * - VITE_AZURE_AUTHORITY: Base authority URL (e.g., https://{tenant}.ciam.login.microsoftonline.com/{tenant-id})
 * - VITE_AZURE_USER_FLOW: User flow/policy name (e.g., B2C_1_signupsignin)
 * 
 * For regular Azure AD:
 * - VITE_AZURE_AUTHORITY: Full authority URL (e.g., https://login.microsoftonline.com/{tenant-id})
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID ?? '',
    authority: buildAuthority(),
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI ?? window.location.origin,
    postLogoutRedirectUri: import.meta.env.VITE_AZURE_POST_LOGOUT_REDIRECT_URI ?? `${window.location.origin}/login`,
    // Known authorities for External ID (helps with token caching and endpoint resolution)
    // For CIAM, this should be the domain (e.g., https://portal.ciam.login.microsoftonline.com)
    // For B2C, this should be the domain (e.g., https://portal.b2clogin.com)
    // IMPORTANT: knownAuthorities should be the base domain WITHOUT tenant-id for CIAM
    // This helps MSAL resolve endpoints correctly and avoid CORS issues
    knownAuthorities: (() => {
      // If explicitly set, use that
      if (import.meta.env.VITE_AZURE_KNOWN_AUTHORITIES) {
        const authorities = import.meta.env.VITE_AZURE_KNOWN_AUTHORITIES.split(',').map((a: string) => a.trim());
        if (import.meta.env.DEV) {
          console.log('[MSAL Config] Using explicit knownAuthorities:', authorities);
        }
        return authorities;
      }
      
      // For External ID, extract the domain
      if (isExternalId()) {
        const domain = getKnownAuthorityDomain();
        if (domain) {
          if (import.meta.env.DEV) {
            console.log('[MSAL Config] Auto-detected knownAuthority:', domain);
          }
          return [domain];
        }
      }
      
      return undefined;
    })(),
  },
  cache: {
    // IMPORTANT: Cache location is a configuration strategy decision, not a bug fix.
    // Changing this requires: (1) verifying all MSAL invariants are met,
    // (2) confirming SSO issues after invariants are fixed, (3) security review approval.
    // See MSAL_CACHE_LOCALSTORAGE_CHANGE.md for change documentation.
    cacheLocation: 'localStorage', // Enables cross-tab SSO and cross-app SSO (shared across tabs/origin)
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    // Conditional async popups: Only enable when popup fallback is explicitly enabled
    // 
    // asyncPopups helps avoid Cross-Origin-Opener-Policy (COOP) issues by using postMessage
    // for popup communication instead of window.closed monitoring. However, it can cause
    // postMessage serialization errors when MSAL tries to pass non-cloneable data.
    //
    // Since the app primarily uses redirect flow (default), asyncPopups is not needed
    // for normal operation. We only enable it when popup fallback is explicitly enabled
    // via VITE_ENABLE_MSAL_POPUP_FALLBACK or runtime flag MSAL_POPUP_FALLBACK.
    //
    // Redirect flow doesn't use popups, so asyncPopups can be safely disabled.
    // If popup fallback is needed, asyncPopups helps with COOP but may still cause
    // postMessage errors - these are non-fatal and can be handled gracefully.
    asyncPopups: shouldEnableMsalPopupFallback(),
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            if (import.meta.env.DEV) {
              console.info(message);
            }
            return;
          case 3: // LogLevel.Verbose
            if (import.meta.env.DEV) {
              console.debug(message);
            }
            return;
        }
      },
      logLevel: import.meta.env.DEV ? 2 : 0, // LogLevel.Info in dev, LogLevel.Error in prod
    },
  },
};

/**
 * Identity Provider types supported
 */
export type IdentityProvider = 'microsoft' | 'google' | 'apple' | 'idme';

/**
 * Identity Provider configuration
 * Maps IdP types to their domain hints or custom parameters
 */
export const identityProviders: Record<IdentityProvider, { domainHint?: string; name: string; icon: string }> = {
  microsoft: {
    name: 'Microsoft',
    icon: 'fab fa-microsoft',
  },
  google: {
    domainHint: 'google.com',
    name: 'Google',
    icon: 'fab fa-google',
  },
  apple: {
    domainHint: 'apple.com',
    name: 'Apple',
    icon: 'fab fa-apple',
  },
  idme: {
    domainHint: 'id.me',
    name: 'Id.me',
    icon: 'fas fa-id-card',
  },
};

/**
 * Build API scope for Entra External ID (CIAM)
 * Format: api://<backend-api-client-id>/access_as_user
 * This scope is used when the backend API is registered in Entra External ID
 * 
 * The backend API client ID can be:
 * 1. Explicitly set via VITE_AZURE_API_CLIENT_ID (recommended - separate backend API app)
 * 2. Fallback to VITE_AZURE_CLIENT_ID (if using same app registration for frontend and backend)
 */
export function buildApiScope(): string {
  // Prefer backend API client ID if explicitly set (separate app registration)
  const backendApiClientId = import.meta.env.VITE_AZURE_API_CLIENT_ID;
  const frontendClientId = import.meta.env.VITE_AZURE_CLIENT_ID ?? '';
  
  // Use backend API client ID if provided, otherwise fallback to frontend client ID
  const apiClientId = backendApiClientId || frontendClientId;
  
  if (!apiClientId) {
    console.warn('[MSAL Config] Neither VITE_AZURE_API_CLIENT_ID nor VITE_AZURE_CLIENT_ID is set. Cannot build API scope.');
    return '';
  }
  
  // For CIAM, use the api://<client-id>/access_as_user format
  // This matches the scope exposed by the backend API in Entra External ID
  // Reference: https://github.com/victormaxi/Microsoft-CIAM
  const scope = `api://${apiClientId}/access_as_user`;
  
  if (import.meta.env.DEV) {
    if (backendApiClientId) {
      console.log('[MSAL Config] Using backend API client ID for scope:', scope);
    } else {
      console.log('[MSAL Config] Using frontend client ID for scope (consider setting VITE_AZURE_API_CLIENT_ID):', scope);
    }
  }
  
  return scope;
}

/**
 * Get scopes for authentication request
 * For Entra External ID (CIAM), uses api://<client-id>/access_as_user
 * For regular Azure AD, uses configured scopes or defaults to User.Read
 */
export function getAuthScopes(): string[] {
  const usingExternalId = isExternalId();
  
  // For Entra External ID (CIAM), use the API scope
  if (usingExternalId) {
    const apiScope = buildApiScope();
    if (apiScope) {
      if (import.meta.env.DEV) {
        console.log('[MSAL Config] Using CIAM API scope:', apiScope);
      }
      return [apiScope];
    }
  }
  
  // For regular Azure AD or if API scope cannot be built, use configured scopes
  const envScopes = import.meta.env.VITE_AZURE_SCOPES;
  if (envScopes) {
    return envScopes.split(',').map((s: string) => s.trim());
  }
  
  // Default scope for regular Azure AD
  return ['User.Read'];
}

/**
 * Create login request with optional IdP domain hint
 * For Azure Entra External ID, supports user flow/policy and identity provider selection
 */
export function createLoginRequest(idp?: IdentityProvider): PopupRequest | RedirectRequest {
  const userFlow = getUserFlow();
  const usingExternalId = isExternalId();
  const baseAuthority = getBaseAuthority();
  
  const request: PopupRequest | RedirectRequest = {
    scopes: getAuthScopes(),
    account: undefined,
  };

  // For External ID, add authority with user flow if specified
  if (usingExternalId && userFlow) {
    // For custom CIAM domains (.ciamlogin.com), don't use authority with user flow
    // Instead, pass user flow as a query parameter
    if (isCiamCustomDomain(baseAuthority)) {
      // Set base authority without user flow
      request.authority = baseAuthority;
      // Add user flow as query parameter for custom CIAM domains
      request.extraQueryParameters = {
        ...request.extraQueryParameters,
        p: userFlow, // 'p' parameter is used for policies/user flows in CIAM
      };
      if (import.meta.env.DEV) {
        console.log('[MSAL] Using custom CIAM domain - user flow passed as query param:', userFlow);
      }
    } else {
      // For B2C and standard CIAM, use authority with user flow appended
      request.authority = buildAuthority();
    }
  }

  // For External ID, configure identity provider selection
  if (usingExternalId && idp && idp !== 'microsoft') {
    // For custom CIAM domains (.ciamlogin.com), domain_hint must be the IdP display name
    // as configured in the tenant (e.g. 'Google'), not a generic domain ('google.com').
    if (isCiamCustomDomain(baseAuthority)) {
      // For custom CIAM domains, domain_hint must match the IdP display name configured
      // in the tenant (e.g. 'Google'), not the generic domain ('google.com').
      request.domainHint = identityProviders[idp].name;
      if (import.meta.env.DEV) {
        console.log(`[MSAL] Login request for ${identityProviders[idp].name}:`, {
          authority: request.authority,
          domainHint: request.domainHint,
        });
      }
    } else if (identityProviders[idp]?.domainHint) {
      // Standard CIAM / B2C: domain_hint works for social providers
      request.domainHint = identityProviders[idp].domainHint;
    }
  } else if (idp && idp !== 'microsoft' && identityProviders[idp]?.domainHint) {
    // Regular Azure AD (not External ID): use domain hint
    request.domainHint = identityProviders[idp].domainHint;
  }

  return request;
}

/**
 * Scopes required for authentication
 * Add additional scopes as needed for your application
 */
export const loginRequest: PopupRequest | RedirectRequest = createLoginRequest();

/**
 * Silent token request configuration
 * Used for token refresh
 * For External ID, includes authority with user flow if needed
 */
export function createSilentRequest(account: AccountInfo): SilentRequest {
  if (!account) {
    throw new Error('Account is required for silent token acquisition');
  }
  
  const userFlow = getUserFlow();
  const usingExternalId = isExternalId();
  const baseAuthority = getBaseAuthority();
  
  const request: SilentRequest = {
    scopes: getAuthScopes(),
    account: account,
    forceRefresh: false, // Use cached tokens when possible
  };

  // For External ID, add authority with user flow if specified
  if (usingExternalId && userFlow) {
    // For custom CIAM domains (.ciamlogin.com), don't use authority with user flow
    // Instead, pass user flow as a query parameter
    if (isCiamCustomDomain(baseAuthority)) {
      // Set base authority without user flow
      request.authority = baseAuthority;
      // Add user flow as claims parameter for custom CIAM domains
      request.extraQueryParameters = {
        ...request.extraQueryParameters,
        p: userFlow,
      };
    } else {
      // For B2C and standard CIAM, use authority with user flow appended
      request.authority = buildAuthority();
    }
  }

  return request;
}

// Note: createSilentRequest now requires an AccountInfo parameter
// Use createSilentRequest(account) directly instead of this default export

/**
 * Graph API endpoint for user profile
 */
export const graphConfig = {
  graphMeEndpoint: import.meta.env.VITE_GRAPH_ME_ENDPOINT ?? 'https://graph.microsoft.com/v1.0/me',
};

/**
 * Validate MSAL configuration
 * Validates both Azure AD and Azure Entra External ID configurations
 */
export function validateMsalConfig(): boolean {
  if (!msalConfig.auth.clientId) {
    console.error('VITE_AZURE_CLIENT_ID is not set. MSAL authentication will not work.');
    return false;
  }

  if (!msalConfig.auth.authority) {
    console.error('VITE_AZURE_AUTHORITY is not set. MSAL authentication will not work.');
    return false;
  }

  // For External ID, validate configuration
  if (isExternalId()) {
    const authority = msalConfig.auth.authority;
    const baseAuthority = getBaseAuthority();
    
    // Validate CIAM authority format
    if (isCiamLoginDomain(authority)) {
      // CIAM format: https://{tenant-name}.ciam.login.microsoftonline.com/{tenant-id}
      const ciamMatch = baseAuthority.match(/^https:\/\/([^.]+)\.ciam\.login\.microsoftonline\.com\/([^/]+)$/);
      if (!ciamMatch) {
        console.error(
          'Invalid CIAM authority format. Expected: https://{tenant-name}.ciam.login.microsoftonline.com/{tenant-id}\n' +
          `Current: ${baseAuthority}\n` +
          'Please verify your VITE_AZURE_AUTHORITY in .env file.'
        );
        return false;
      }
      
      const tenantName = ciamMatch[1];
      
      // Warn if tenant name looks like a placeholder
      if (tenantName && (tenantName === 'yourtenant' || tenantName === 'portal' || tenantName.length < 3)) {
        console.warn(
          `Warning: Tenant name "${tenantName}" in authority URL might be incorrect.\n` +
          'For Azure Entra External ID CIAM, the tenant name should match your actual tenant.\n' +
          'You can find the correct authority URL in Azure Portal:\n' +
          '1. Go to Azure Portal -> Azure AD B2C (or Entra External ID)\n' +
          '2. Go to App registrations -> Your app -> Overview\n' +
          '3. Check the "Authority" or "Endpoint" field'
        );
      }
      
      // Note: .ciamlogin.com is a valid format for Azure Entra External ID (custom domain)
      // We don't need to reject it, but we should validate it's properly formatted
      if (isCiamCustomDomain(baseAuthority) && !isCiamLoginDomain(baseAuthority)) {
        // Validate the custom CIAM domain format: https://{tenant}.ciamlogin.com/{tenant-id}
        const ciamCustomMatch = baseAuthority.match(/^https:\/\/([^.]+)\.ciamlogin\.com\/([^/]+)$/);
        if (!ciamCustomMatch) {
          console.error(
            '\n❌ Invalid Custom CIAM Authority URL Format\n' +
            `Current: ${baseAuthority}\n` +
            'For custom CIAM domain, the format should be: "https://{tenant}.ciamlogin.com/{tenant-id}"\n' +
            'Please verify your VITE_AZURE_AUTHORITY in .env file.\n'
          );
          return false;
        }
        if (import.meta.env.DEV) {
          console.log('[MSAL Config] Using custom CIAM domain format:', baseAuthority);
        }
      }
      
      // Validate user flow is provided
      if (!getUserFlow()) {
        console.warn(
          'Azure Entra External ID (CIAM) detected but VITE_AZURE_USER_FLOW is not set.\n' +
          'User flow is required for CIAM authentication. Please set VITE_AZURE_USER_FLOW in your .env file.'
        );
      }
    }
    
    // Validate B2C authority format
    if (isB2CLoginDomain(authority) && !getUserFlow()) {
      console.warn(
        'Azure Entra External ID (B2C) detected but VITE_AZURE_USER_FLOW is not set. ' +
        'User flow should be specified in the authority URL or as an environment variable.'
      );
    }
  }

  return true;
}

